import React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { AvailabilityPicker } from "@/components/availability-picker";
import type { AvailabilityWindow } from "@/components/availability-picker";
import Button from "@/components/button";
import { Combobox } from "@/components/combobox";
import { postWorkerAvailability } from "@/queries/api/searchApi";
import { useGetProfileQuery } from "@/queries/hooks/profileHooks";
import { QueryKeys } from "@/queries/queryKeys";
import { notify } from "@/utils";
import { useOrderedRoleOptions } from "@/utils/hooks/use-ordered-role-options";

export function AvailabilityPanel() {
  const { t } = useTranslation("settings");
  const profileQuery = useGetProfileQuery();

  if (profileQuery.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <h3 className="text-xl font-semibold text-garnet mb-2">
          {t("availability.title")}
        </h3>
        <p className="body-small text-garnet">
          {t("actions.loading", { ns: "common" })}
        </p>
      </div>
    );
  }

  return (
    <AvailabilityForm
      initialRoles={profileQuery.data?.profile.preferred_roles ?? []}
      initialWindows={profileQuery.data?.profile.posted_availability ?? []}
    />
  );
}

function AvailabilityForm({
  initialRoles,
  initialWindows,
}: {
  initialRoles: Array<string>;
  initialWindows: Array<AvailabilityWindow>;
}) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const rolesOptions = useOrderedRoleOptions();

  const [selectedRoles, setSelectedRoles] =
    React.useState<Array<string>>(initialRoles);
  const [windows, setWindows] =
    React.useState<Array<AvailabilityWindow>>(initialWindows);

  const handleWindowsChange = React.useCallback(
    (w: Array<AvailabilityWindow>) => setWindows(w),
    [],
  );

  const mutation = useMutation({
    mutationFn: postWorkerAvailability,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PROFILE] });
      notify.success(t("availability.saved"));
    },
    onError: (err: { message?: string }) => {
      notify.error(err.message || t("availability.saveError"));
    },
  });

  const minDate = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const canSubmit =
    selectedRoles.length > 0 && windows.length > 0 && !mutation.isPending;

  function handleSubmit() {
    if (selectedRoles.length === 0) {
      notify.error(t("availability.selectRole"));
      return;
    }
    if (windows.length === 0) {
      notify.error(t("availability.selectAvailability"));
      return;
    }
    mutation.mutate({ role_names: selectedRoles, windows });
  }

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold text-garnet mb-2">
        {t("availability.title")}
      </h3>

      <section className="flex flex-col gap-2">
        <label className="body-small text-garnet font-semibold">
          {t("availability.rolesLabel")}
        </label>
        <Combobox
          multi
          options={rolesOptions}
          value={selectedRoles}
          onChange={(v) => setSelectedRoles(v)}
          placeholder={t("availability.rolesPlaceholder")}
          searchPlaceholder={t("availability.rolesSearchPlaceholder")}
        />
      </section>

      <section className="flex flex-col gap-2">
        <label className="body-small text-garnet font-semibold">
          {t("availability.availabilityLabel")}
        </label>
        <AvailabilityPicker
          minDate={minDate}
          initialWindows={initialWindows}
          showContinue={false}
          onChange={handleWindowsChange}
        />
      </section>

      <div className="flex justify-end pt-2">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {mutation.isPending
            ? t("availability.saving")
            : t("actions.saveChanges", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
